import React from 'react';
import axios from 'axios';
import querystring from 'querystring-es3';
import Draggable from 'react-draggable';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import APIManager from '../../actions/APIManager';
import { logout, clearApiKey } from '../../actions/GlobalActions';
import concatURL from '../../helpers/concatURL';
import { superAttestDone, superAttestCancelled } from '../../helpers/superAttest';
import '../Common/Common.scss';

// REQ-2026-10-16-5401: the super-user ticket number + name dialog.
//
// Built from NotificationsModal.js (Phil, 2026-10-16) so it looks like the rest of V5.1:
// the house `dialog-box` / `box-header` / `box-body` shell (Common/DialogBox.scss) inside
// Draggable, with no backdrop. A draggable, non-modal dialog is fine: the SERVER refuses
// every call made with the pending key, so nothing behind it works until the user attests.
//
// What changed from that shell, on purpose:
//   - no close X and no close-on-unmount -- the only exits are Continue and Cancel, and
//     Cancel signs out;
//   - the Records body is replaced by the two required fields (Continue stays disabled
//     until both have something in them), an inline line for the server's 400 message,
//     and the two buttons;
//   - nothing is pre-filled or remembered: the name is asked every time (decision 2).
//
// The text is the SUPPORT AUTHORIZATION NOTICE the API sends (auth.superAttestNotice:
// {version, date, text}); the server logs which version the sign-in was shown. Rendered as
// plain text, one paragraph per blank-line-separated block. Only when the API sent none
// does the dialog fall back to the short line it used to carry.
//
// Shown whenever auth.superAttestRequired is set -- see helpers/superAttest.js for the
// three ways that happens. Mounted once, in AppContainer.

const initialState = { ticket: '', name: '', busy: false, error: null };

// Paragraphs of a notice's plain text: a blank line separates them; a single line break
// inside one is kept as a space.
function noticeParagraphs(notice) {
  const text = notice && typeof notice.text === 'string' ? notice.text : '';
  return text
    .split(/\r?\n\s*\r?\n/)
    .map(p => p.replace(/\s*\r?\n\s*/g, ' ').trim())
    .filter(Boolean);
}

class SuperAttestModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = { ...initialState };
  }

  componentDidUpdate(prevProps) {
    // A fresh prompt starts empty -- never carry a previous name over.
    if (!prevProps.open && this.props.open) {
      this.setState({ ...initialState });
    }
  }

  canSubmit() {
    return this.state.ticket.trim() !== '' && this.state.name.trim() !== '' && !this.state.busy;
  }

  handleSubmit = e => {
    e.preventDefault();
    if (!this.canSubmit()) return;
    this.setState({ busy: true, error: null });
    APIManager.getKey()
      .then(key =>
        axios.request({
          url: concatURL('/auth/super_attest'),
          method: 'POST',
          headers: { 'X-API-KEY': key, 'Content-Type': 'application/x-www-form-urlencoded' },
          data: querystring.stringify({ ticket: this.state.ticket.trim(), name: this.state.name.trim() }),
        }),
      )
      .then(response => {
        if (response.data && Number(response.data.success) === 1) {
          this.setState({ ...initialState });
          superAttestDone();
        } else {
          this.setState({
            busy: false,
            error: (response.data && response.data.errmsg) || 'Could not finish signing in.',
          });
        }
      })
      .catch(err => {
        // 400 = a field is missing (the key stays pending); 401 = the key is gone or
        // expired, and Cancel (sign out) is the way on. Either way: say why, stay open.
        const msg =
          (err && err.response && err.response.data && err.response.data.errmsg) ||
          (typeof err === 'string' ? err : err && err.message) ||
          'Could not reach the server. Check your connection and try again.';
        this.setState({ busy: false, error: msg });
      });
  };

  handleCancel = () => {
    this.setState({ busy: true });
    superAttestCancelled();
    // Sign out: delete the pending key server-side (best effort), then clear the stored
    // key and cookies whatever that call did -- logout() alone swallows its own failure.
    Promise.resolve(this.props.actions.logout())
      .catch(() => {})
      .then(() => this.props.actions.clearApiKey())
      .then(() => {
        this.setState({ ...initialState });
        window.location.assign('/login' + window.location.search);
      });
  };

  // Escape must not close it.
  swallowEscape = e => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  render() {
    if (!this.props.open) {
      return '';
    }
    const { ticket, name, busy, error } = this.state;
    const { notice } = this.props;
    const paragraphs = noticeParagraphs(notice);
    const style = { color: 'black', top: '10%', left: 'calc(50% - 240px)', right: 'auto', width: '480px', maxWidth: 'calc(100vw - 32px)' };

    return (
      <Draggable cancel=".formDialog">
        <div
          className="dialog-box"
          style={style}
          role="dialog"
          aria-modal="false"
          aria-labelledby="super-attest-title"
          aria-describedby="super-attest-desc"
          onKeyDown={this.swallowEscape}
        >
          <div className={'box-header'}>{this.renderControls()}</div>
          <div className={'box-body formDialog'} style={{ color: 'black', padding: '15px' }}>
            {paragraphs.length > 0 && (
              <p style={{ margin: '0 0 8px', textAlign: 'center', fontWeight: 'bold' }}>Authorized use only</p>
            )}
            {paragraphs.length > 0 ? (
              <div
                id="super-attest-desc"
                data-notice-version={notice.version}
                tabIndex={0}
                style={{
                  maxHeight: '55vh',
                  overflowY: 'auto',
                  border: '1px solid #ddd',
                  background: '#f7f7f7',
                  padding: '8px 12px',
                  fontSize: '13px',
                  lineHeight: 1.45,
                }}
              >
                {paragraphs.map((p, i) => (
                  <p key={i} style={{ margin: i === 0 ? 0 : '8px 0 0' }}>
                    {p}
                  </p>
                ))}
              </div>
            ) : (
              <p id="super-attest-desc" style={{ marginTop: 0 }}>
                You are signing in with the shared support account. Enter the ticket you are working and your own name to
                continue.
              </p>
            )}
            {paragraphs.length > 0 && (
              <p style={{ margin: '4px 0 8px', fontSize: '11px', color: '#666', textAlign: 'right' }}>
                Authorization notice, version {notice.version}
                {notice.date ? ` (${notice.date})` : ''}
              </p>
            )}
            <form onSubmit={this.handleSubmit} autoComplete="off">
              <TextField
                id="super-attest-ticket"
                label="Ticket number"
                value={ticket}
                onChange={e => this.setState({ ticket: e.target.value, error: null })}
                required
                fullWidth
                autoFocus
                margin="dense"
                size="small"
                disabled={busy}
                inputProps={{ maxLength: 100, autoComplete: 'off' }}
              />
              <TextField
                id="super-attest-name"
                label="Your name"
                value={name}
                onChange={e => this.setState({ name: e.target.value, error: null })}
                required
                fullWidth
                margin="dense"
                size="small"
                disabled={busy}
                inputProps={{ maxLength: 100, autoComplete: 'off' }}
              />
              {error && (
                <div role="alert" style={{ color: '#a90329', margin: '8px 0' }}>
                  {error}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
                <Button variant="outlined" onClick={this.handleCancel} disabled={busy}>
                  Cancel and sign out
                </Button>
                <Button variant="contained" type="submit" disabled={!this.canSubmit()}>
                  {busy ? 'Checking...' : 'Continue'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </Draggable>
    );
  }

  renderControls() {
    // NotificationsModal's title row, minus the close button.
    return (
      <div className="box-controllers">
        <span className="dialogbox-title" id="super-attest-title">
          Support sign-in
        </span>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    open: !!state.APIReducer.auth.superAttestRequired,
    notice: state.APIReducer.auth.superAttestNotice || null,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ logout, clearApiKey }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SuperAttestModal);
